import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import styles from './Tooltip.module.scss';

export const Tooltip = ({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) => {
  const [leftDistanceState, setLeftDistance] = useState<number>(0);
  const [rightDistanceState, setRightDistance] = useState<number>(0);
  const tooltipDiv = useRef<HTMLDivElement>(null);
  const elementWidth = tooltipDiv.current?.getBoundingClientRect().width ?? 0;
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const elementLeft = useRef(0);
  const elementRight = useRef(0);
  const sidePadding = 20;

  useEffect(() => {
    elementLeft.current = tooltipDiv.current?.getBoundingClientRect().left ?? 0;
    elementRight.current =
      tooltipDiv.current?.getBoundingClientRect().right ?? 0;

    // check if space left is less than padding
    if (active && elementLeft.current <= 0) {
      setLeftDistance(Math.abs(elementLeft.current) + sidePadding);
    } else if (active && elementLeft.current <= sidePadding) {
      setLeftDistance(sidePadding);
    }

    // checking if there is enough space for tooltip and padding at the right
    if (active && elementRight.current > screenWidth) {
      setRightDistance(
        Math.abs(elementRight.current - screenWidth) + sidePadding
      );
    } else if (active && elementRight.current + sidePadding > screenWidth) {
      setRightDistance(sidePadding);
    }

    if (!active) {
      // resetting element position to center in order to calculate
      // next appearence relatively to original position
      // otherwise calculations will cause misplacement
      const timer = setTimeout(() => {
        setLeftDistance(0);
        setRightDistance(0);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [active]);

  const getStyles = () => {
    if (
      (elementLeft.current === 0 || elementLeft.current < screenWidth / 2) &&
      elementRight.current < screenWidth
    ) {
      return {
        left: `calc(50% - ${elementWidth / 2}px)`,
        marginLeft: `${leftDistanceState}px`,
      };
    } else if (
      elementRight.current > screenWidth ||
      elementLeft.current > screenWidth / 2
    ) {
      return {
        right: `calc(50% - ${elementWidth / 2}px)`,
        marginRight: `${rightDistanceState}px`,
      };
    }
  };

  return (
    <div
      ref={tooltipDiv}
      className={clsx(styles.tooltip__wrapper, active && 'active')}
      style={getStyles()}
      role="tooltip"
    >
      {children}
    </div>
  );
};
